// Pure, React-free helpers for the DataTable drag-reorder + show/hide feature.
// Kept out of data-table.tsx so they unit-test with `node --import tsx --test` without
// loading React/JSX. See docs/plans/2026-07-09-table-column-customization-*.

/**
 * Reserved key for the synthetic row-selection column. When a DataTable is `selectable`,
 * the checkbox column is folded into the column model under this key so it participates in
 * drag-reorder like any column. It is never hideable and is excluded from the columns menu.
 */
export const SELECT_COLUMN_KEY = '__select__';

/**
 * Readable fallback label for a column that declares no `label` (e.g. an icon/badge or the
 * selection column), used in the show/hide menu. "eomOhQty" -> "Eom Oh Qty",
 * "ship_city" -> "Ship City".
 */
export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Apply a user's saved column `order` (list of `col.key`) and `hidden` set to the live
 * `columns` array, returning the columns to render, in order.
 *
 * Rules (LOCKED — see PLAN.md):
 *  1. Honor `order`: place each column named in `order` by key; skip keys not present in the
 *     live set (dropped/renamed columns) and any duplicate references within `order`.
 *  2. Any live column NOT named in `order` is inserted at a DETERMINISTIC declared position:
 *     immediately before the first already-placed column whose original declared index is
 *     greater than the new column's; otherwise appended. So a newly-added column lands where
 *     the developer declared it — even after the user has reordered — never jammed far-right.
 *  3. `hidden` is applied LAST, so a hidden column keeps its ordering slot and re-showing it
 *     restores its place.
 *  4. Non-mutating: the input `columns` array is never reordered in place (protects the
 *     caller's memoized / compose-time array, e.g. ControlTower's insertAfter()).
 *  5. Duplicate keys make key-based reorder ambiguous and could silently drop a column, so we
 *     fail loud in dev and DISABLE reorder for that table (return every column, visibility
 *     only) rather than risk data loss.
 */
export function deriveOrderedColumns<C extends { key: string }>(
  columns: C[],
  order: string[],
  hidden: string[],
): C[] {
  const hiddenSet = hidden.length ? new Set(hidden) : null;
  const applyHidden = (arr: C[]): C[] =>
    hiddenSet ? arr.filter((c) => !hiddenSet.has(c.key)) : arr;

  // Rule 5 — duplicate-key guard.
  const keys = columns.map((c) => c.key);
  if (keys.length !== new Set(keys).size) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error(
        '[DataTable] duplicate column key(s) detected — column reorder disabled for this table:',
        keys,
      );
    }
    return applyHidden(columns.slice());
  }

  const byKey = new Map(columns.map((c) => [c.key, c] as const));
  const declaredIndex = new Map(columns.map((c, i) => [c.key, i] as const));
  const placed: C[] = [];
  const placedKeys = new Set<string>();

  // Rule 1 — honor saved order.
  for (const key of order) {
    if (placedKeys.has(key)) continue;
    const col = byKey.get(key);
    if (col) {
      placed.push(col);
      placedKeys.add(key);
    }
  }

  // Rule 2 — insert unsaved live columns at their deterministic declared position.
  for (const col of columns) {
    if (placedKeys.has(col.key)) continue;
    const di = declaredIndex.get(col.key)!;
    let insertAt = placed.length;
    for (let i = 0; i < placed.length; i++) {
      // Servwell patch (102): `!` — this tsconfig runs noUncheckedIndexedAccess; the
      // index is loop-bounded. No behavior change.
      if (declaredIndex.get(placed[i]!.key)! > di) {
        insertAt = i;
        break;
      }
    }
    placed.splice(insertAt, 0, col);
    placedKeys.add(col.key);
  }

  // Rule 3 — visibility last.
  return applyHidden(placed);
}

/**
 * Move the key `fromKey` to the position of `toKey` within an ordered key list, returning the
 * new order as a fresh array; `currentOrder` is never mutated. Both keys must be in
 * `currentOrder`; otherwise a copy of it comes back unchanged.
 *
 * Nothing here is about columns: the data table's header drag-and-drop and arrow keys use it
 * for column keys, and the service types card for its row ids.
 */
export function reorderKeys(
  currentOrder: readonly string[],
  fromKey: string,
  toKey: string,
): string[] {
  const from = currentOrder.indexOf(fromKey);
  const to = currentOrder.indexOf(toKey);
  if (fromKey === toKey || from === -1 || to === -1) return currentOrder.slice();
  const next = currentOrder.slice();
  next.splice(from, 1);
  next.splice(to, 0, fromKey);
  return next;
}
